// This function return always an object containing dataset details.
import {
  cloneDeep, every, filter, forEach, get, includes, indexOf, isArray, isEqual, isEqualWith, isNull, isObject,
  isString, isUndefined, map, orderBy, split, trim, uniqBy, uniqWith } from "lodash-es";
import { $parseEvent, $parseExpr, isDefined } from "@wm/core";
import {ToDatePipe} from "@wm/components-base";
import { DataSetItem } from "./dataset-aware-form.component";

const ALLFIELDS = 'All Fields';
const isSearchWidget = (widgetType: any) => {
  return widgetType === 'wm-search';
};

export const convertDataToObject = (dataResult: any) => {
  if (isString(dataResult)) {
    dataResult = split(dataResult, ',').map(str => str.trim());
  }

  return dataResult;
};

/**
 * function to get the ordered dataset based on the given orderby
 */
export const getOrderedDataset = (dataSet: any, orderby: string, innerItem?: string) => {
  if (!orderby) {
    return cloneDeep(dataSet);
  }

  // The order by only works when the dataset contains list of objects.
  const items = orderby.split(','),
    fields: any = [],
    directions: any = [];
  items.forEach(obj => {
    const item = obj.split(':');
    fields.push(innerItem ? innerItem + '.' + item[0] : item[0]);
    directions.push(item[1]);
  });
  return orderBy(dataSet, fields, directions);
};

/**
 * Returns an array of object, each object contain the DataSetItem whose key, value, label are extracted from object keys.
 */
export const transformDataWithKeys = (dataSet: any) => {
  const data: DataSetItem[] = [];
  // if the dataset is instance of object (not an array) or the first item in the dataset array is an object,
  // then we extract the keys from the object and prepare the dataset items.
  if (isObject(dataSet[0]) || (isObject(dataSet) && !(dataSet instanceof Array))) {
    // getting keys of the object
    const objectKeys = Object.keys(dataSet[0] || dataSet);
    forEach(objectKeys, (objKey, index) => {
      data.push({
        key: objKey,
        label: objKey,
        value: objKey,
        index: index + 1
      });
    });
  }

  return data;
};

/**
 * The first step in datasetItems creation is data transformation:
 *
 * The dataset can contain one of the following formats and each of them to be converted to the given format;
 *
 * 1) The comma separated string..eg: A,B,C => [{ key: 'A', value: 'A'}, { key: 'B', value: 'B'}, { key: 'C', value: 'C'}]
 * 2) The array of values eg: [1,2,3] => [{ key: 1, value: 1}, { key: 2, value: 2}, { key: 3, value: 3}]
 * 3) an object eg: {name: 'A', age: 20} => [ {key: 'name', value: 'A'}, {key: 'age', value: 20}]
 * 4) an array of objects...eg: [ {name: 'A', age: 20}, {name: 'B', age: 20}] ==> returns [{key: _DATAFIELD_, value: _DISPLAYFIELD, label: _DISPLAYVALUE}]
 */
export const transformFormData = (context: any, dataSet: any, dataField: string = '', displayOptions?: any, startIndex?: number, scope?: any): Array<DataSetItem> => {
  const data: any = [];
  if (!dataSet) {
    // return;
  }

  dataSet = convertDataToObject(dataSet);

  // startIndex is the index of the next new item.
  if (isUndefined(startIndex)) {
    startIndex = 1;
  }

  if (isString(dataSet)) {
    dataSet = dataSet.split(',').map(str => str.trim());
    dataSet.forEach((option: any, index: any) => {
      const dataSetItem = {
        key: option,
        value: option,
        label: (isDefined(option) && option !== null) ? option.toString() : '',
        index: startIndex + index
      };
      setGroupbyKey(scope, context, dataSetItem, 'value');
      data.push(dataSetItem);
    });
  } else if (isArray(dataSet) && !isObject(dataSet[0])) { // array of primitive values only
    dataSet.forEach((option: any, index: any) => {
      const dataSetItem = {
        key: option,
        value: option,
        label: (isDefined(option) && option !== null) ? option.toString() : '',
        index: startIndex + index
      };
      setGroupbyKey(scope, context, dataSetItem, 'value');
      data.push(dataSetItem);
    });
  } else if (!(dataSet instanceof Array) && isObject(dataSet)) {
    const i = 0;
    forEach(dataSet, (value, key) => {
      const dataSetItem: any = {
        key: trim(key),
        value: key,
        label: (isDefined(value) && value !== null) ? value : '',
        index: startIndex,
        dataObject: dataSet
      };
      setGroupbyKey(scope, context, dataSetItem, 'value');
      data.push(dataSetItem);
    });
  } else {
    if (!dataField) { // consider the datafield as 'ALLFIELDS' when datafield is not given.
      dataField = ALLFIELDS;
    }
    dataSet.forEach((option: any, index: any) => {
      const key = dataField === ALLFIELDS ? startIndex + index : getObjValueByKey(option, dataField);
      // Omit all the items whose datafield (key) is null or undefined.
      if (!isUndefined(key) && !isNull(key)) {
        const label = getEvaluatedData(option, {
          field: displayOptions.displayField,
          expression: displayOptions.displayExpr
        }, context);
        const dataSetItem = {
          key: key,
          label: (isDefined(label) && label !== null) ? label.toString() : '',
          value: dataField === ALLFIELDS ? option : key,
          dataObject: option, // represents the object when datafield is ALLFIELDS. This is used as innerItem while grouping the datasetItems.
          index: startIndex + index
        };
        if (displayOptions.displayImgSrc) {
          (dataSetItem as any).imgSrc = getEvaluatedData(option, {
            expression: displayOptions.displayImgSrc,
          }, context);
        }
        setGroupbyKey(scope, context, dataSetItem, 'dataObject');
        data.push(dataSetItem);
      }
    });
  }
  return data;
};

// This function is used to set the groupby field for search/autocomplete
const setGroupbyKey = (scope: any, context: any, dataSetItem: any, innerItem: any) => {
  if (scope && isSearchWidget(scope.widgetType)) {
    if (scope.groupby) {
      if (includes(scope.groupby, '(')) {
        //[Todo-CSP]: can generate expr fn in page based on groupby property on widget
        const groupDataByUserDefinedFn = $parseEvent(scope.groupby);
        (dataSetItem as any).groupby = groupDataByUserDefinedFn(context, {'row': dataSetItem.dataObject || dataSetItem});
      } else {
        (dataSetItem as any).groupby = groupDataByField(scope.groupby, scope.match, innerItem, scope.dateformat, scope.datePipe, scope.appDefaults, dataSetItem);
      }
    } else {
      (dataSetItem as any).groupby = '';
    }
  }
};
const groupDataByField = (groupby: string, match: string, innerItem: string, dateFormat: string, datePipe: ToDatePipe, AppDefaults: any, liData: Object) => {
}

/**
 * Returns the value for the provided key in the object
 */
export const getObjValueByKey = (obj: any, strKey: string) => {
  /* check for the key-string */
  if (strKey) {
    let val: any;
    /* convert indexes to properties, so as to work for even 'key1[0].child1'*/
    strKey.replace(/\[(\w+)\]/g, '.$1').split('.').forEach(key => {
      // If obj is null, then assign val to null.
      val = (val && val[key]) || (isNull(obj) ? obj : obj[key]);
    });
    return val;
  }
  return obj;
};

/**
 * returns the display field data for any dataset widgets
 * Based on the bind display expression or display expression or display name,
 * data is extracted and formatted from the passed option object
 * If there is field is specified, field value is obtained from the dataObj.
 * If expression is given, evaluates the expression value.
 * else check for bindExpression, extract the value from the dataObj
 */
export const getEvaluatedData = (dataObj: any, options: any, context?: any) => {
  let expressionValue;
  const field = options.field,
    expr = options.expression,
    bindExpr = options.bindExpression;

  // if key is bound expression
  if (bindExpr) {
    // remove 'bind:' prefix from the boundExpressionName
    expressionValue = bindExpr.replace('bind:', '');

    // For bind expression passing the last argument that the object of dataset item.
    // Note: For parse expression we are sending dataObj with the propery name "__1", we used the same propery to send the last argument as dataset item
    if (checkIsCustomPipeExpression(expressionValue)) {
      expressionValue = expressionValue + ': __1';
    }
    // parse the expressionValue for replacing all the expressions with values in the object
    expressionValue = getUpdatedExpr(expressionValue);
  } else {
    expressionValue = expr ? expr : field;
  }

  // Handling field name with special charecters
  // Ex: field = "f name"
  if (!bindExpr && !expr) {
    return get(dataObj, field);
  }

  return $parseExpr(expressionValue)(context, Object.assign({}, dataObj, {__1: dataObj}));
};

/**
 * exp: User built bind expression
 *  To check the custom pipe expressions.
 *  If user use the custom pipe expression it will check in the exp. that "custom" string exist or not with (:) syntax.
 *  Eg: custom:
 *      custom    :
 */
export const checkIsCustomPipeExpression = function (exp: any) {
  let customRegEx = /(custom(\s*:))/g;
  let matches = exp.match(customRegEx);
  return matches && matches.length;
}

const getUpdatedExpr = (expr: string) => {
  let updated = '', ch, next, i, j, matchCh, matchCount, isQuotedStr, subStr, isQuotedStrEvaluated;

  expr = expr.replace(/\$\[data\[\$i\]/g, '$[__1');

  for (i = 0; i < expr.length; i++) {
    ch = expr[i];
    next = expr[i + 1];

    /**
     * if the expression starts with $[, check the next(ch) character,
     *    if ch is a quote(', ") change the expr to __[
     *    if ch is a whiteSpace, remove it
     *    else remove $[
     */
    if (ch === '$' && next === '[') {
      matchCount = 1;
      isQuotedStrEvaluated = false;
      isQuotedStr = false;

      for (j = i + 2; j < expr.length; j++) {

        matchCh = expr[j];

        if (matchCh === ' ') {
          continue;
        }

        if (!isQuotedStrEvaluated) {
          isQuotedStr = expr[j] === '"' || expr[j] === '\'';
          isQuotedStrEvaluated = true;
        }

        if (matchCh === '[') {
          matchCount++;
        } else if (matchCh === ']') {
          matchCount--;
        }

        if (!matchCount) {
          subStr = expr.substring(i + 2, j);
          if (isQuotedStr) {
            updated += '__1[' + subStr + ']';
          } else {
            updated += subStr;
          }

          break;
        }
      }
      i = j;
    } else {
      updated += ch;
    }
  }

  return updated;
};

/**
 * Private method to get the unique objects by the data field
 */
export const getUniqObjsByDataField = (data: Array<DataSetItem>, dataField: string, displayField: string, allowEmptyFields?: boolean) => {
  let uniqData;
  let ALLFIELDS;
  const isAllFields = dataField === ALLFIELDS;

  uniqData = isAllFields ? uniqWith(data, isEqual) : uniqBy(data, 'key');

  if (!displayField || allowEmptyFields) {
    return uniqData;
  }

  // return objects having non empty datafield and display field values.
  return filter(uniqData, (obj) => {
    if (isAllFields) {
      return trim(obj.label);
    }
    return trim(obj.key) && trim(obj.label);
  });
};

// Converts any type of data to array.
export const extractDataAsArray = (data: any) => {

  if (isUndefined(data) || isNull(data) || trim(data) === '') {
    return [];
  }

  if (isString(data)) {
    data = split(data, ',').map(str => str.trim());
  }

  if (!isArray(data)) {
    data = [data];
  }

  return data;
};


/*Add this in  Utils.ts */
export const isEqualWithFields = (obj1: any, obj2: any, compareBy: any) => {
  // compareBy can be 'id' or 'id1, id2' or 'id1, id2:id3'
  // Split the compareby comma separated values
  let _compareBy = isArray(compareBy) ? compareBy : split(compareBy, ',');

  _compareBy = map(_compareBy, trim);

  return isEqualWith(obj1, obj2, function (o1, o2) {
    return every(_compareBy, function (cb) {
      let cb1, cb2, _cb;

      // If compareby contains : , compare the values by the keys on either side of :
      let compareBySeparator;
      if (indexOf(cb, compareBySeparator) === -1) {
        cb1 = cb2 = trim(cb);
      } else {
        _cb = split(cb, compareBySeparator);
        cb1 = trim(_cb[0]);
        cb2 = trim(_cb[1]);
      }

      return get(o1, cb1) === get(o2, cb2);
    });
  });
};
